"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { getCandlePatternInfo, CandlePatternInfo } from "@/lib/candlePatternInfo";
import { CandleApiResponse, CandleMeta, CandleSetupGroup, DataItem, DivergenceItem, TabData } from "@/types";
import { api } from "@/lib/api";
import { formatDate, formatPatternName } from "@/components/tabs/utils";

export const TAB_VALUES = ["patterns", "doji", "structure", "reversal", "divergences", "trend"] as const;
export type TabValue = typeof TAB_VALUES[number];

export const TIMEFRAME_VALUES = ["daily", "weekly", "monthly"] as const;
export type TimeframeValue = typeof TIMEFRAME_VALUES[number];

export const TAB_META: Record<TabValue, { eyebrow: string; title: string; subtitle: string }> = {
  patterns: {
    eyebrow: "Pattern Workspace",
    title: "Candlestick Pattern Scanner",
    subtitle: "Review live candle families, check the trend context, and open the names that matter first.",
  },
  doji: {
    eyebrow: "Doji Workspace",
    title: "Doji Scanner",
    subtitle: "Track neutral dojis and bearish-exhaustion doji reversals without digging through the full candle catalog.",
  },
  structure: {
    eyebrow: "Structure Workspace",
    title: "Daily Structure Scanner",
    subtitle: "Identify key structure levels and breakouts in the current trading session.",
  },
  reversal: {
    eyebrow: "Reversal Workspace",
    title: "Reversal Analysis Scanner",
    subtitle: "Spot potential reversal setups using candle structure and trend context.",
  },
  divergences: {
    eyebrow: "Divergence Workspace",
    title: "RSI Divergence Scanner",
    subtitle: "Find bullish and bearish RSI divergences across the market for confluence setups.",
  },
  trend: {
    eyebrow: "Trend Workspace",
    title: "Trend Filter",
    subtitle: "Filter the universe by moving-average position and trend weakness.",
  },
};

const getPreferredSelection = (setupGroups: CandleSetupGroup[], fallbackTypes: string[]) =>
  setupGroups[0]?.raw_types?.[0] || fallbackTypes[0] || null;

const isDojiType = (value: unknown) => String(value || "").trim().toUpperCase().includes("DOJI");

interface DataTabsStateOptions {
  onDataLoaded?: (data: TabData) => void;
  initialTab?: TabValue;
}

export function useDataTabsState({ onDataLoaded, initialTab = "patterns" }: DataTabsStateOptions) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const searchParamsString = searchParams?.toString() ?? "";

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const tabParam = searchParams?.get("tab");
    return TAB_VALUES.includes(tabParam as TabValue) ? (tabParam as TabValue) : initialTab;
  });

  const [selectedCandleType, setSelectedCandleType] = useState<string | null>(null);
  const [selectedDojiType, setSelectedDojiType] = useState<string | null>(null);
  const [selectedStructureType, setSelectedStructureType] = useState<string | null>(null);
  const [selectedReversalType, setSelectedReversalType] = useState<string | null>(null);
  const [selectedPatternForKnowledge, setSelectedPatternForKnowledge] = useState<CandlePatternInfo | null>(null);

  const [trendFilter, setTrendFilter] = useState<"all" | "up" | "down">("all");
  const [divergenceFilter, setDivergenceFilter] = useState<"all" | "bullish" | "bearish">("all");
  const [divergenceClassFilter, setDivergenceClassFilter] = useState<"all" | "reversal" | "continuation">(() => {
    const classParam = searchParams?.get("divergenceClass");
    return classParam === "reversal" || classParam === "continuation" ? classParam : "all";
  });
  const [symbolQuery, setSymbolQuery] = useState<string>(searchParams?.get("symbol") || "");
  const [timeframe, setTimeframe] = useState<TimeframeValue>(() => {
    const tfParam = searchParams?.get("timeframe");
    return TIMEFRAME_VALUES.includes(tfParam as TimeframeValue) ? (tfParam as TimeframeValue) : "daily";
  });

  const [data, setData] = useState<TabData>({ candlePatterns: [], dailyStructure: [], reversal: [] });
  const [candleMeta, setCandleMeta] = useState<CandleMeta>({});
  const [candleViews, setCandleViews] = useState<CandleApiResponse["views"]>({});
  const [divergencesData, setDivergencesData] = useState<DivergenceItem[]>([]);
  const [divergencesFetched, setDivergencesFetched] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParamsString);
    params.set("tab", activeTab);
    params.set("timeframe", timeframe);
    if (symbolQuery) params.set("symbol", symbolQuery);
    else params.delete("symbol");
    if (divergenceClassFilter !== "all") params.set("divergenceClass", divergenceClassFilter);
    else params.delete("divergenceClass");
    const nextQuery = params.toString();
    if (nextQuery !== searchParamsString) {
      router.replace(`${pathname}?${nextQuery}`, { scroll: false });
    }
  }, [activeTab, timeframe, symbolQuery, divergenceClassFilter, pathname, router, searchParamsString]);

  // Sync initialTab prop when URL has no tab param
  useEffect(() => {
    if (!searchParams?.get("tab")) {
      setActiveTab(initialTab);
    }
  }, [initialTab, searchParams]);

  // Data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const candlesData: unknown = await api.get<unknown>("/api/outputs/candles.json");

        const candlesObject =
          typeof candlesData === "object" && candlesData !== null
            ? (candlesData as Record<string, unknown>)
            : null;
        const candlePayload = candlesObject as CandleApiResponse | null;

        const results = Array.isArray(candlesData)
          ? candlesData
          : (candlesObject?.data as DataItem[] | undefined) ||
            (candlesObject?.results as DataItem[] | undefined) ||
            [];

        setCandleMeta({
          scan_date: candlePayload?.scan_date,
          timeframe: candlePayload?.timeframe,
          generated_at: candlePayload?.generated_at,
        });
        setCandleViews(candlePayload?.views || {});

        const newData = { candlePatterns: results, dailyStructure: results, reversal: results };
        setData(newData);
        onDataLoaded?.(newData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [onDataLoaded, timeframe]);

  // Lazy-fetch divergences only when the tab is first visited for a given timeframe
  useEffect(() => {
    if (activeTab !== "divergences") return;
    const cacheKey = timeframe;
    if (divergencesFetched.has(cacheKey)) return;

    const fetchDivergences = async () => {
      try {
        const divergencesJsonData = await api.post<Record<string, unknown>>("/api/divergences", {
          min_strength: 0,
          limit: 200,
          debug: false,
          timeframe,
        });
        const divResults = Array.isArray(divergencesJsonData?.results)
          ? (divergencesJsonData.results as DivergenceItem[])
          : [];
        setDivergencesData(divResults);
        setDivergencesFetched((prev) => new Set(prev).add(cacheKey));
      } catch (divErr) {
        console.warn("Divergences data not available:", divErr);
        setDivergencesData([]);
      }
    };

    void fetchDivergences();
  }, [activeTab, timeframe, divergencesFetched]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Derived data
  const normalizedSymbolQuery = useMemo(() => symbolQuery.trim().toUpperCase(), [symbolQuery]);

  const patternSetupGroups = useMemo(() => candleViews?.patterns?.setup_groups || [], [candleViews]);
  const dojiSetupGroups = useMemo(
    () =>
      patternSetupGroups.filter(
        (group) => isDojiType(group.key) || group.raw_types.some((rawType) => isDojiType(rawType))
      ),
    [patternSetupGroups]
  );
  const structureSetupGroups = useMemo(() => candleViews?.structure?.setup_groups || [], [candleViews]);
  const reversalSetupGroups = useMemo(() => candleViews?.reversal?.setup_groups || [], [candleViews]);

  const candleItems = useMemo(
    () => data.candlePatterns.filter((item) => !normalizedSymbolQuery || (item.symbol as string | undefined || "").toUpperCase().includes(normalizedSymbolQuery)),
    [data.candlePatterns, normalizedSymbolQuery]
  );

  const structureItemsRaw = useMemo(
    () => data.dailyStructure.filter((item) => !normalizedSymbolQuery || (item.symbol as string | undefined || "").toUpperCase().includes(normalizedSymbolQuery)),
    [data.dailyStructure, normalizedSymbolQuery]
  );

  const dojiItems = useMemo(
    () =>
      candleItems.filter(
        (item) => isDojiType(item.candle_type) || isDojiType(item.base_candle_type) || isDojiType(item.candle_key)
      ),
    [candleItems]
  );

  const reversalItemsRaw = useMemo(
    () => data.reversal.filter((item) => !normalizedSymbolQuery || (item.symbol as string | undefined || "").toUpperCase().includes(normalizedSymbolQuery)),
    [data.reversal, normalizedSymbolQuery]
  );

  const divergencesBase = useMemo(
    () => divergencesData.filter((item) => !normalizedSymbolQuery || (item.symbol || "").toUpperCase().includes(normalizedSymbolQuery)),
    [divergencesData, normalizedSymbolQuery]
  );

  const uniqueCandleTypes = useMemo(
    () => Array.from(new Set(candleItems.map((i) => i.candle_type || i.type).filter((t): t is string => !!t))).sort(),
    [candleItems]
  );

  const uniqueStructureTypes = useMemo(
    () => Array.from(new Set(structureItemsRaw.flatMap((i) => (Array.isArray(i.structure) ? i.structure : [])).filter((t): t is string => !!t))).sort(),
    [structureItemsRaw]
  );

  const uniqueDojiTypes = useMemo(
    () => Array.from(new Set(dojiItems.map((i) => i.candle_type || i.type).filter((t): t is string => !!t))).sort(),
    [dojiItems]
  );

  const uniqueReversalTypes = useMemo(
    () => Array.from(new Set(reversalItemsRaw.flatMap((i) => (Array.isArray(i.reversal) ? i.reversal : [])).filter((t): t is string => !!t))).sort(),
    [reversalItemsRaw]
  );

  // Auto-select initial types when data arrives
  useEffect(() => {
    if (data.candlePatterns.length > 0 && !selectedCandleType) {
      const t = getPreferredSelection(patternSetupGroups, uniqueCandleTypes);
      if (t) { setSelectedCandleType(t); setSelectedPatternForKnowledge(getCandlePatternInfo(t)); }
    }
  }, [data.candlePatterns.length, patternSetupGroups, selectedCandleType, uniqueCandleTypes]);

  useEffect(() => {
    if (data.dailyStructure.length > 0 && !selectedStructureType) {
      const t = getPreferredSelection(structureSetupGroups, uniqueStructureTypes);
      if (t) setSelectedStructureType(t);
    }
  }, [data.dailyStructure.length, selectedStructureType, structureSetupGroups, uniqueStructureTypes]);

  useEffect(() => {
    if (dojiItems.length > 0 && !selectedDojiType) {
      const t = getPreferredSelection(dojiSetupGroups, uniqueDojiTypes);
      if (t) setSelectedDojiType(t);
    }
  }, [dojiItems.length, dojiSetupGroups, selectedDojiType, uniqueDojiTypes]);

  useEffect(() => {
    if (data.reversal.length > 0 && !selectedReversalType) {
      const t = getPreferredSelection(reversalSetupGroups, uniqueReversalTypes);
      if (t) setSelectedReversalType(t);
    }
  }, [data.reversal.length, reversalSetupGroups, selectedReversalType, uniqueReversalTypes]);

  const structureItems = useMemo(
    () => structureItemsRaw.filter((item) => Array.isArray(item.structure) && item.structure.length > 0),
    [structureItemsRaw]
  );

  const reversalItems = useMemo(
    () => reversalItemsRaw.filter((item) => Array.isArray(item.reversal) && item.reversal.length > 0),
    [reversalItemsRaw]
  );

  const latestDataDate = useMemo((): string => {
    if (candleMeta.scan_date) return candleMeta.scan_date;
    const allDates = data.candlePatterns.map((i) => i.date).filter(Boolean);
    if (allDates.length === 0) return "No date available";
    return (allDates as string[]).sort().reverse()[0];
  }, [candleMeta.scan_date, data.candlePatterns]);

  const activeItemCount = useMemo<string | number>(() => {
    if (activeTab === "patterns") return candleItems.length;
    if (activeTab === "doji") return dojiItems.length;
    if (activeTab === "structure") return structureItems.length;
    if (activeTab === "reversal") return reversalItems.length;
    if (activeTab === "divergences") return divergencesBase.length;
    return "Dynamic";
  }, [activeTab, candleItems.length, divergencesBase.length, dojiItems.length, reversalItems.length, structureItems.length]);

  const activeTypeCount = useMemo(() => {
    if (activeTab === "patterns") {
      if (normalizedSymbolQuery) return uniqueCandleTypes.length;
      return candleViews?.patterns?.summary.setup_family_count ?? candleViews?.patterns?.summary.family_count ?? uniqueCandleTypes.length;
    }
    if (activeTab === "doji") {
      return normalizedSymbolQuery ? uniqueDojiTypes.length : dojiSetupGroups.length || uniqueDojiTypes.length;
    }
    if (activeTab === "structure") return normalizedSymbolQuery ? uniqueStructureTypes.length : candleViews?.structure?.summary.family_count ?? uniqueStructureTypes.length;
    if (activeTab === "reversal") return normalizedSymbolQuery ? uniqueReversalTypes.length : candleViews?.reversal?.summary.family_count ?? uniqueReversalTypes.length;
    if (activeTab === "divergences") return 2;
    return 4;
  }, [activeTab, candleViews, dojiSetupGroups.length, normalizedSymbolQuery, uniqueCandleTypes.length, uniqueDojiTypes.length, uniqueReversalTypes.length, uniqueStructureTypes.length]);

  const topFocusLine = useMemo(() => {
    if (!normalizedSymbolQuery && activeTab === "patterns" && candleViews?.patterns?.summary.top_setup_line)
      return `Current focus: ${candleViews.patterns.summary.top_setup_line}`;
    if (activeTab === "doji") {
      const dojiGroups = dojiSetupGroups
        .slice(0, 3)
        .map((group) => `${group.display_name} (${group.count})`)
        .join(" · ");
      return dojiGroups
        ? `Current focus: ${dojiGroups}`
        : "Current focus: isolate doji prints and bearish-exhaustion doji setups.";
    }
    if (!normalizedSymbolQuery && activeTab === "structure" && candleViews?.structure?.summary.top_setup_line)
      return `Current focus: ${candleViews.structure.summary.top_setup_line}`;
    if (!normalizedSymbolQuery && activeTab === "reversal" && candleViews?.reversal?.summary.top_setup_line)
      return `Current focus: ${candleViews.reversal.summary.top_setup_line}`;

    const counts = new Map<string, number>();
    const accumulate = (values: string[]) => values.forEach((v) => counts.set(formatPatternName(v), (counts.get(formatPatternName(v)) || 0) + 1));

    if (activeTab === "structure") structureItemsRaw.forEach((item) => accumulate(Array.isArray(item.structure) ? item.structure : []));
    else if (activeTab === "patterns") candleItems.forEach((item) => { const raw = typeof item.candle_type === "string" ? item.candle_type : typeof item.type === "string" ? item.type : ""; if (raw) accumulate([raw]); });
    else if (activeTab === "reversal") reversalItemsRaw.forEach((item) => accumulate(Array.isArray(item.reversal) ? item.reversal : []));
    else if (activeTab === "divergences") {
      const bullish = divergencesBase.filter((item) => item.type?.includes("BULLISH")).length;
      const bearish = divergencesBase.filter((item) => item.type?.includes("BEARISH")).length;
      return `Bullish divergences ${bullish} · Bearish divergences ${bearish}`;
    } else if (activeTab === "trend") {
      return "Current focus: filter the universe by moving-average position and trend weakness.";
    }

    const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
    if (!top.length) return "No active setup concentration yet.";
    return `Current focus: ${top.map(([label, count]) => `${label} (${count})`).join(" · ")}`;
  }, [activeTab, candleItems, candleViews, divergencesBase, dojiItems, dojiSetupGroups, normalizedSymbolQuery, reversalItemsRaw, structureItemsRaw]);

  const latestDataLabel = useMemo(() => formatDate(latestDataDate) || latestDataDate, [latestDataDate]);

  return {
    // Tab
    activeTab, setActiveTab,
    // Filters
    timeframe, setTimeframe,
    symbolQuery, setSymbolQuery,
    trendFilter, setTrendFilter,
    divergenceFilter, setDivergenceFilter,
    divergenceClassFilter, setDivergenceClassFilter,
    // Selections
    selectedCandleType, setSelectedCandleType,
    selectedDojiType, setSelectedDojiType,
    selectedStructureType, setSelectedStructureType,
    selectedReversalType, setSelectedReversalType,
    selectedPatternForKnowledge, setSelectedPatternForKnowledge,
    // Share
    copied, handleShare,
    // Data
    loading, error, candleViews,
    // Computed
    candleItems, dojiItems, structureItemsRaw, reversalItemsRaw,
    structureItems, reversalItems, divergencesBase,
    patternSetupGroups, dojiSetupGroups, structureSetupGroups, reversalSetupGroups,
    uniqueCandleTypes, uniqueDojiTypes, uniqueStructureTypes, uniqueReversalTypes,
    latestDataDate, latestDataLabel,
    activeItemCount, activeTypeCount, topFocusLine,
    activeMeta: TAB_META[activeTab],
  };
}
